import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SectionWrapper from "@/components/base/SectionWrapper";
import { supabase, type Post } from "@/lib/supabase";

type Category = "all" | "news" | "event" | "announcement";

export default function EventsPage() {
  const [activeCategory, setActiveCategory] = useState<Category>("all");
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const { data } = await supabase
        .from("posts")
        .select("*")
        .eq("status", "published")
        .order("created_at", { ascending: false });
      setPosts(data || []);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const categories: { key: Category; label: string }[] = [
    { key: "all", label: "All" },
    { key: "event", label: "Events" },
    { key: "news", label: "News" },
    { key: "announcement", label: "Announcements" },
  ];

  const filtered = activeCategory === "all"
    ? posts
    : posts.filter((p) => p.category === activeCategory);

  const getCategoryColor = (cat: string) => {
    switch (cat) { case "event": return "bg-gold-500"; case "news": return "bg-primary-600"; default: return "bg-emerald-600"; }
  };
  const getCategoryLabel = (cat: string) => {
    switch (cat) { case "event": return "Event"; case "news": return "News"; default: return "Announcement"; }
  };

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="w-10 h-10 border-2 border-primary-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden bg-primary-900">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative z-10 text-center px-4">
          <p className="text-gold-400 text-xs font-medium uppercase tracking-[2px] mb-3">Latest Updates</p>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white">News &amp; Events</h1>
        </div>
      </section>

      <SectionWrapper className="py-12 md:py-20 px-4 md:px-8 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center mb-10 md:mb-14">
            <div className="inline-flex items-center p-1 bg-gray-100 rounded-full gap-1">
              {categories.map((cat) => (
                <button key={cat.key} onClick={() => setActiveCategory(cat.key)} className={`px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${activeCategory === cat.key ? "bg-white text-primary-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>{cat.label}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((post) => {
              const isExternal = Boolean(post.external_url);
              const cardClasses = "group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col cursor-pointer";

              const CardContent = (
                <>
                  <div className="relative h-48 md:h-52 overflow-hidden">
                    <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium text-white ${getCategoryColor(post.category)}`}>{getCategoryLabel(post.category)}</span>
                    {isExternal && (
                      <span className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium text-white bg-black/50 backdrop-blur-sm inline-flex items-center gap-1">
                        <i className="ri-external-link-line" /> External
                      </span>
                    )}
                  </div>
                  <div className="p-5 md:p-6 flex-1 flex flex-col">
                    {post.event_date ? (
                      <div className="inline-flex items-center gap-1.5 mb-2">
                        <i className="ri-calendar-event-line text-gold-500 text-xs" />
                        <span className="text-gold-600 text-xs font-semibold">
                          {new Date(post.event_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                        </span>
                        <span className="text-gray-300 text-xs mx-1">·</span>
                        <span className="text-gray-400 text-xs">Published {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-xs mb-2">{new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
                    )}
                    <h3 className="font-heading font-bold text-base md:text-lg text-primary-900 mb-3 group-hover:text-gold-700 transition-colors line-clamp-2">{post.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-3 mb-4 flex-1">{post.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 text-gold-600 text-sm font-medium group-hover:gap-2.5 transition-all">
                      {isExternal ? "View on External Site" : "Read More"}
                      <i className={isExternal ? "ri-external-link-line" : "ri-arrow-right-line"} />
                    </span>
                  </div>
                </>
              );

              return isExternal ? (
                <a
                  key={post.id}
                  href={post.external_url!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardClasses}
                >
                  {CardContent}
                </a>
              ) : (
                <Link key={post.id} to={`/events/${post.slug}`} className={cardClasses}>
                  {CardContent}
                </Link>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4"><i className="ri-article-line text-2xl text-gray-400" /></div>
              <p className="text-gray-500">No posts found in this category.</p>
            </div>
          )}
        </div>
      </SectionWrapper>
    </div>
  );
}